import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Modal,
  Descriptions,
  Grid,
  Divider,
  Form,
  InputNumber,
  Alert,
} from "antd";

import {
  SearchOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";
import { DownloadExcel } from "../../components/ExcelFile";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const theme = {
  primary: "var(--theme1, #eca413)",
  primaryDark: "var(--theme1Dark, #132272)",
  border: "rgba(19, 34, 114, 0.12)",
  muted: "rgba(0,0,0,0.45)",
};

const Bookings = () => {
  const { request } = useRequest();
  const screens = useBreakpoint();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const authRole = localStorage.getItem("authRole") || "admin";
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [airlineOptions, setAirlineOptions] = useState([]);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [mealOptions, setMealOptions] = useState([]);
  const [createForm] = Form.useForm();
  const [roomAssignForm] = Form.useForm();
  const [acceptModalVisible, setAcceptModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [resendingSMS, setResendingSMS] = useState(false);

  let airlineId = "";
  try {
    const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    airlineId = profile?._id || profile?.id || "";
  } catch (e) { }


  const requestAsync = React.useMemo(() => ({ url, method, data }) =>
    new Promise((resolve, reject) => {
      request({
        url,
        method,
        data,
        onSuccess: (res) => resolve(res),
        onError: (err) => reject(err),
      });
    }), [request]);

  const getManagerId = () => {
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
      return profile?.Hotel_Id || loginData?.managerDetails?.Hotel_Id || null;
    } catch (_err) {
      return null;
    }
  };

  const fetchBookings = React.useCallback(async () => {
    setLoading(true);
    try {
      if (authRole === "admin") {
        const data = await requestAsync({
          url: `${apiPath.baseURL}/getAllBookings`,
          method: "GET",
        });
        if (data?.success && Array.isArray(data.bookings)) {
          setBookings(data.bookings);
        } else {
          setBookings([]);
        }
      } else if (authRole === "hotel") {
        const managerId = getManagerId();
        if (!managerId) {
          setBookings([]);
          return;
        }

        const hotelsRes = await requestAsync({
          url: `${apiPath.baseURL}/get_Hotels_of_HotelPartner/${managerId}`,
          method: "GET",
        });

        const managerHotels =
          hotelsRes?.Hotel_Details ||
          hotelsRes?.Hotels_details ||
          hotelsRes?.data ||
          [];

        if (!Array.isArray(managerHotels) || managerHotels.length === 0) {
          setBookings([]);
          return;
        }

        const bookingResponses = await Promise.all(
          managerHotels.map((hotel) =>
            requestAsync({
              url: `${apiPath.baseURL}/getAllBookings_of_Hotel/${hotel.Hotel_Id}`,
              method: "GET",
            }).catch(() => ({ success: false, bookings: [] }))
          )
        );

        const merged = bookingResponses.flatMap((res) =>
          Array.isArray(res?.bookings) ? res.bookings : []
        );
        const unique = Array.from(
          new Map(merged.map((b) => [b._id || b.Booking_Id, b])).values()
        );
        setBookings(unique);
      } else if (authRole === "airline") {
        const hotelsRes = await requestAsync({
          url: `${apiPath.baseURL}${apiPath.airlineHotels}`,
          method: "GET",
        });

        const airlineHotels = hotelsRes?.hotels || [];

        if (!Array.isArray(airlineHotels) || airlineHotels.length === 0) {
          setBookings([]);
          return;
        }

        const bookingResponses = await Promise.all(
          airlineHotels.map((hotel) =>
            requestAsync({
              url: `${apiPath.baseURL}/getAllBookings_of_Hotel/${hotel.Hotel_Id}`,
              method: "GET",
            }).catch(() => ({ success: false, bookings: [] }))
          )
        );

        const merged = bookingResponses.flatMap((res) =>
          Array.isArray(res?.bookings) ? res.bookings : []
        );
        const unique = Array.from(
          new Map(merged.map((b) => [b._id || b.Booking_Id, b])).values()
        );
        setBookings(unique);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Error:", error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [authRole, requestAsync]);

  const handleOpenAccept = (record) => {
    setSelectedBooking(record);
    roomAssignForm.resetFields();
    setAvailableRoomTypes([]);
    setAcceptModalVisible(true);

    request({
      url: `${apiPath.baseURL}${apiPath.getRoomInventory}/${record.Hotel_Id}`,
      method: "GET",
      onSuccess: (data) => {
        if (data && data.rooms) {
          setAvailableRoomTypes(data.rooms.filter(r => r.status !== 0 && r.Available_rooms > 0));
        }
      },
    });
  };



  const handleAcceptConfirm = (values) => {
    if (!selectedBooking) return;
    setAcceptLoading(true);

    const payload = {
      roomType: values.roomType
    };

    request({
      url: apiPath.assignRoomToBooking(selectedBooking.Booking_Id),
      method: "POST",
      data: payload,
      onSuccess: () => {
        ShowToast("Booking accepted and room assigned", Severty.SUCCESS);
        setAcceptModalVisible(false);
        fetchBookings();
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || "Failed to accept booking",
          Severty.ERROR
        );
      },
      finally: () => setAcceptLoading(false),
    });
  };

  const handleOpenReject = (record) => {
    setSelectedBooking(record);
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = () => {
    if (!selectedBooking) return;
    setRejectLoading(true);
    request({
      url: apiPath.rejectBooking(selectedBooking.Booking_Id),
      method: "POST",
      onSuccess: () => {
        ShowToast("Booking rejected successfully", Severty.SUCCESS);
        setRejectModalVisible(false);
        fetchBookings();
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || "Failed to reject booking",
          Severty.ERROR
        );
      },
      finally: () => setRejectLoading(false),
    });
  };

  const handleResendSMS = () => {
    if (!selectedBooking) return;
    setResendingSMS(true);
    request({
      url: apiPath.resendBookingSMS(selectedBooking.Booking_Id),
      method: "POST",
      onSuccess: (res) => {
        ShowToast("SMS confirmations resent successfully", Severty.SUCCESS);
        if (res?.data) {
          setSelectedBooking(res.data);
          setBookings((prev) =>
            prev.map((b) => (b.Booking_Id === res.data.Booking_Id ? res.data : b))
          );
        }
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || "Failed to resend SMS",
          Severty.ERROR
        );
      },
      finally: () => setResendingSMS(false),
    });
  };


  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    // Fetch airlines
    request({
      url: "/airline/users",
      method: "GET",
      onSuccess: (res) => {
        if (res?.success && Array.isArray(res.users)) {
          setAirlineOptions(res.users.map((u) => ({ value: u._id || u.id, label: u.name })));
        }
      },
      onError: () => setAirlineOptions([]),
    });
  }, [request]);

  const handleCreateModalOpen = () => {
    createForm.resetFields();
    if (authRole === "airline") {
      createForm.setFieldsValue({ airline_id: airlineId, adults: 1, children: 0 });
      handleAirlineChange(airlineId);
    } else {
      createForm.setFieldsValue({ adults: 1, children: 0 });
    }
    setCreateModalVisible(true);
  };

  const handleAirlineChange = (selectedAirlineId) => {
    createForm.setFieldsValue({ Hotel_Id: undefined, bookedRoomsData: [], meals: [] });
    setRoomOptions([]);
    setMealOptions([]);

    request({
      url: `${apiPath.baseURL}${apiPath.hotels}`,
      method: "GET",
      onSuccess: (data) => {
        let allHotels = data.Hotel_details || data.data || [];
        let airlineHotels = allHotels.filter(h => 
          h.airline_id === selectedAirlineId || 
          (h.airline_ids && h.airline_ids.includes(selectedAirlineId))
        );
        setHotelOptions(airlineHotels.map(h => ({ value: h.Hotel_Id, label: h.Hotel_name })));
      },
      onError: () => setHotelOptions([]),
    });
  };

  const handleHotelChange = (hotelId) => {
    createForm.setFieldsValue({ bookedRoomsData: [], meals: [] });

    request({
      url: `${apiPath.baseURL}${apiPath.getRoomInventory}/${hotelId}`,
      method: "GET",
      onSuccess: (data) => {
        if (data && data.rooms) {
          setRoomOptions(data.rooms.map(r => ({
            label: `${r.type} (${r.Available_rooms} available) - $${r.price || "N/A"}`,
            value: r.type,
            price: r.price,
            available: r.Available_rooms
          })));
        } else {
          setRoomOptions([]);
        }
      },
      onError: () => setRoomOptions([]),
    });

    request({
      url: `${apiPath.baseURL}${apiPath.getMeals}/${hotelId}`,
      method: "GET",
      onSuccess: (res) => {
        if (res && res.meals) {
          setMealOptions(res.meals.map(m => ({
            ...m,
            label: `${m.meal_name}`,
            value: m._id
          })));
        } else {
          setMealOptions([]);
        }
      },
      onError: () => setMealOptions([]),
    });
  };

  const handleCreateSubmit = async (values) => {
    const bookedRoomsData = values.bookedRoomsData.map(roomType => {
      const room = roomOptions.find(r => r.value === roomType);
      return {
        roomType: roomType,
        price: room?.price || 0
      };
    });

    const selectedMealsObjects = mealOptions.filter(m => (values.meals || []).includes(m.value)).map(m => ({
      meal_id: m._id,
      meal_name: m.meal_name
    }));

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      dob: values.dob ? values.dob.format("YYYY-MM-DD") : undefined,
      email: values.email,
      phoneNumber: values.phoneNumber,
      airline_id: values.airline_id,
      adults: values.adults || 1,
      children: values.children || 0,
      Hotel_Id: values.Hotel_Id,
      checkIn: values.dates ? values.dates[0].format("YYYY-MM-DD") : undefined,
      checkOut: values.dates ? values.dates[1].format("YYYY-MM-DD") : undefined,
      meals: selectedMealsObjects,
      bookedRoomsData: bookedRoomsData
    };

    setLoading(true);
    request({
      url: `${apiPath.baseURL}${apiPath.createBookingWithGuest}`,
      method: "POST",
      data: payload,
      onSuccess: (res) => {
        ShowToast("Booking created successfully", Severty.SUCCESS);
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchBookings();
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to create booking", Severty.ERROR);
        setLoading(false);
      }
    });
  };

  // Filter bookings based on search, status, and date range
  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.Booking_Id?.toLowerCase().includes(searchText.toLowerCase()) ||
      booking.customer_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      booking.customer_email?.toLowerCase().includes(searchText.toLowerCase()) ||
      booking.customer_phone?.includes(searchText);

    const matchesStatus = !statusFilter || booking.status === statusFilter;

    let matchesDateRange = true;
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const [startDate, endDate] = dateRange;
      const checkInDate = booking.checkIn ? moment(booking.checkIn) : null;
      const checkOutDate = booking.checkOut ? moment(booking.checkOut) : null;

      if (checkInDate && checkOutDate) {
        // Strict boundary logic: Stay must be entirely within the range
        matchesDateRange = checkInDate.isSameOrAfter(startDate, 'day') &&
          checkOutDate.isSameOrBefore(endDate, 'day');
      } else if (checkInDate) {
        // Fallback for bookings with only check-in date
        matchesDateRange = checkInDate.isBetween(startDate, endDate, 'day', '[]');
      } else if (checkOutDate) {
        // Fallback for bookings with only check-out date
        matchesDateRange = checkOutDate.isBetween(startDate, endDate, 'day', '[]');
      }
    }

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const handleExportToExcel = () => {
    if (!filteredBookings || filteredBookings.length === 0) {
      ShowToast("No data available to export", Severty.INFO);
      return;
    }
    const exportData = filteredBookings.map((booking) => ({
      "Booking ID": booking.Booking_Id || "-",
      "Status": (booking.status || "-").toUpperCase(),
      "Hotel Name": booking.Hotel_name || "-",
      "Hotel ID": booking.Hotel_Id || "-",
      "Customer Name": booking.customer_name || "-",
      "Customer Email": booking.customer_email || "-",
      "Customer Phone": booking.customer_phone || "-",
      "Total Guests": booking.totalguests ?? 0,
      "Room Type": booking.roomType || "-",
      "Number of Rooms": booking.number_of_Rooms ?? 0,
      "Check-In Date": booking.checkIn ? moment(booking.checkIn).format("DD MMM YYYY") : "-",
      "Check-Out Date": booking.checkOut ? moment(booking.checkOut).format("DD MMM YYYY") : "-",
      "E-Ticket Link": booking.e_ticket || "N/A",
      "Booked On": booking.createdAt ? moment(booking.createdAt).format("DD MMM YYYY, HH:mm") : "-",
      "Last Updated": booking.updatedAt ? moment(booking.updatedAt).format("DD MMM YYYY, HH:mm") : "-",
    }));
    DownloadExcel(exportData, "Bookings_Report");
  };

  // View booking details
  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setViewModalVisible(true);
  };

  const getLatestInvoiceUrl = async (booking) => {
    if (!booking?.Booking_Id) return booking?.invoice_url || null;
    try {
      const refreshed = await requestAsync({
        url: `${apiPath.baseURL}/refresh_booking_invoice/${booking.Booking_Id}`,
        method: "POST",
      });
      return refreshed?.invoice_url || booking?.invoice_url || null;
    } catch (_err) {
      return booking?.invoice_url || null;
    }
  };

  const openInvoice = async (booking) => {
    if (!booking?.invoice_url) {
      ShowToast("Invoice PDF not available", Severty.INFO);
      return;
    }
    const latestUrl = await getLatestInvoiceUrl(booking);
    if (!latestUrl) {
      ShowToast("Invoice PDF not available", Severty.INFO);
      return;
    }
    const cacheBustedUrl = `${latestUrl}${latestUrl.includes("?") ? "&" : "?"}t=${encodeURIComponent(
      booking.updatedAt || Date.now()
    )}`;
    window.open(cacheBustedUrl, "_blank", "noopener,noreferrer");
  };

  const downloadInvoice = async (booking) => {
    if (!booking?.invoice_url) {
      ShowToast("Invoice PDF not available", Severty.INFO);
      return;
    }
    const latestUrl = await getLatestInvoiceUrl(booking);
    if (!latestUrl) {
      ShowToast("Invoice PDF not available", Severty.INFO);
      return;
    }
    const cacheBustedUrl = `${latestUrl}${latestUrl.includes("?") ? "&" : "?"}t=${encodeURIComponent(
      booking.updatedAt || Date.now()
    )}`;
    const link = document.createElement("a");
    link.href = cacheBustedUrl;
    link.download = `${booking.invoice_number || booking.Booking_Id || "invoice"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      confirmed: "green",
      pending: "orange",
      cancelled: "red",
      completed: "blue",
    };
    return statusColors[status] || "default";
  };

  // Table columns
  const columns = [
    {
      title: "Booking ID",
      dataIndex: "Booking_Id",
      key: "Booking_Id",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "customer_email",
      key: "customer_email",
      render: (text) => (
        <Space>
          <MailOutlined />
          <Text type="secondary">{text || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Phone",
      dataIndex: "customer_phone",
      key: "customer_phone",
      render: (text) => (
        <Space>
          <PhoneOutlined />
          <Text type="secondary">{text || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Hotel",
      dataIndex: "Hotel_name",
      key: "Hotel_name",
    },
    {
      title: "Room Details",
      key: "roomDetails",
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text>Type: {record.roomType}</Text>
          <Text>Rooms: {record.number_of_Rooms}</Text>
          {/* <Text>Fare: ${record.room_fare}</Text> */}
        </Space>
      ),
    },
    {
      title: "Check-in",
      dataIndex: "checkIn",
      key: "checkIn",
      render: (value) => (
        <Space>
          <CalendarOutlined />
          <Text>{value ? moment(value).format("DD MMM YYYY") : "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Check-out",
      dataIndex: "checkOut",
      key: "checkOut",
      render: (value) => (
        <Space>
          <CalendarOutlined />
          <Text>{value ? moment(value).format("DD MMM YYYY") : "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Guests",
      dataIndex: "totalguests",
      key: "totalguests",
      render: (value) => <Text>{value ?? 0}</Text>,
    },
    {
      title: "E-Ticket",
      dataIndex: "e_ticket",
      key: "e_ticket",
      render: (url) => (
        url ? (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(url, "_blank")}
            style={{ padding: 0 }}
          >
            View Ticket
          </Button>
        ) : (
          <Text type="secondary">N/A</Text>
        )
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    // {
    //   title: "Agent",
    //   key: "agent",
    //   render: (_, record) => record.agent_name || record.agent_id || "N/A",
    // },
    // {
    //   title: "Invoice",
    //   key: "invoice",
    //   render: (_, record) => (
    //     <Space>
    //       <Button
    //         size="small"
    //         icon={<FilePdfOutlined />}
    //         onClick={() => openInvoice(record)}
    //         disabled={!record.invoice_url}
    //       >
    //         View PDF
    //       </Button>
    //       <Button
    //         size="small"
    //         icon={<DownloadOutlined />}
    //         onClick={() => downloadInvoice(record)}
    //         disabled={!record.invoice_url}
    //       >
    //         Download
    //       </Button>
    //     </Space>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      // fixed: "right",
      // width: 140,
      render: (_, record) => (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewBookingDetails(record)}
            style={{ width: "100%", textAlign: "left" }}
          >
            View Details
          </Button>
          {authRole === "hotel" && record.status === "pending" && (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                onClick={() => handleOpenAccept(record)}
              >
                Accept
              </Button>
              <Button
                type="primary"
                size="small"
                danger
                onClick={() => handleOpenReject(record)}
              >
                Reject
              </Button>
            </Space>
          )}
        </Space>
      ),
    },

  ];

  return (
    <div
      className="bookings-page"
    >
      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          boxShadow: "0 1px 2px rgba(19,34,114,0.04), 0 12px 40px rgba(19,34,114,0.08)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          }}
        />
        <div style={{ marginBottom: "20px" }}>
          <Title level={screens.xs ? 4 : 3} style={{ marginBottom: 4, fontWeight: 700 }}>
            Bookings Management
          </Title>
          <Text type="secondary">View and manage all hotel bookings</Text>
        </div>


        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={8}>
            <Input
              placeholder="Search by Booking ID, Customer Name, Email, Phone"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="confirmed">Confirmed</Option>
              <Option value="pending">Pending</Option>
              <Option value="cancelled">Cancelled</Option>
              <Option value="completed">Completed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={10} xl={7}>
            <RangePicker
              placeholder={["Check-in Date", "Check-out Date"]}
              value={dateRange}
              onChange={setDateRange}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={12} lg={7} xl={7} style={{ display: 'flex', gap: '8px' }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportToExcel}
              style={{
                flex: 1,
                borderRadius: 10,
                background: "#52c41a",
                borderColor: "#52c41a",
                fontWeight: 600,
              }}
            >
              Export to Excel
            </Button>
            {/* {authRole === "admin" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateModalOpen}
                style={{
                  flex: 1,
                  borderRadius: 10,
                  background: theme.primaryDark,
                  borderColor: theme.primaryDark,
                  fontWeight: 600,
                }}
              >
                Create Booking
              </Button>
            )} */}
            {/* <Button
              type="primary"
              onClick={fetchBookings}
              loading={loading}
              style={{
                flex: 1,
                borderRadius: 10,
                background: theme.primary,
                borderColor: theme.primary,
                fontWeight: 600,
              }}
            >
              Refresh
            </Button> */}
          </Col>
        </Row>

        <Divider style={{ margin: "0 0 14px", borderColor: theme.border }} />

        {/* Bookings Table */}
        <Table
          columns={columns}
          dataSource={filteredBookings}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} bookings`,
          }}
          className="bookings-table"
        />


        {/* Booking Details Modal */}
        <Modal
          title={`Booking Details - ${selectedBooking?.Booking_Id}`}
          open={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          centered
          footer={[
            <Button key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>,
          ]}
          width={screens.xs ? "95%" : 800}
          bodyStyle={{ padding: screens.xs ? 12 : 24 }}
        >
          {selectedBooking && (
            <div>
              <Descriptions
                bordered
                column={screens.xs ? 1 : 2}
                size={screens.xs ? "small" : "default"}
                labelStyle={{ width: screens.xs ? 110 : "auto" }}
              >
                <Descriptions.Item label="Booking ID">
                  <span style={{ wordBreak: "break-all" }}>{selectedBooking.Booking_Id}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(selectedBooking.status)}>
                    {selectedBooking.status?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Hotel">
                  {selectedBooking.Hotel_name}
                </Descriptions.Item>
                <Descriptions.Item label="Hotel ID">
                  {selectedBooking.Hotel_Id}
                </Descriptions.Item>
                {/* <Descriptions.Item label="Agent ID">
                  <span style={{ wordBreak: "break-all" }}>{selectedBooking.agent_id}</span>
                </Descriptions.Item> */}
                {/* <Descriptions.Item label="Agent Name">
                  <span style={{ wordBreak: "break-all" }}>
                    {selectedBooking.agent_name || "N/A"}
                  </span>
                </Descriptions.Item> */}
                {/* <Descriptions.Item label="Promo Code">
                  {selectedBooking.promoCode || "N/A"}
                </Descriptions.Item> */}
                {/* <Descriptions.Item label="Invoice Number">
                  {selectedBooking.invoice_number || "N/A"}
                </Descriptions.Item> */}
                {/* <Descriptions.Item label="Payment Status">
                  <Tag color={selectedBooking.payment_status === "paid" ? "green" : "orange"}>
                    {(selectedBooking.payment_status || "pending").toUpperCase()}
                  </Tag>
                </Descriptions.Item> */}
                {/* <Descriptions.Item label="Invoice PDF" span={2}>
                  <Space>
                    <Button
                      size="small"
                      icon={<FilePdfOutlined />}
                      onClick={() => openInvoice(selectedBooking)}
                      disabled={!selectedBooking.invoice_url}
                    >
                      View PDF
                    </Button>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => downloadInvoice(selectedBooking)}
                      disabled={!selectedBooking.invoice_url}
                    >
                      Download PDF
                    </Button>
                  </Space>
                </Descriptions.Item> */}
              </Descriptions>

              <Title level={5} style={{ marginTop: 24 }}>
                Customer Information
              </Title>
              <Descriptions
                bordered
                column={screens.xs ? 1 : 2}
                size={screens.xs ? "small" : "default"}
                labelStyle={{ width: screens.xs ? 110 : "auto" }}
              >
                <Descriptions.Item label="Name">
                  {selectedBooking.customer_name}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  <span style={{ wordBreak: "break-all" }}>{selectedBooking.customer_email}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <span style={{ wordBreak: "break-all" }}>{selectedBooking.customer_phone}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Total Guests">
                  {selectedBooking.totalguests}
                </Descriptions.Item>
                <Descriptions.Item label="E-Ticket">
                  {selectedBooking.e_ticket ? (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => window.open(selectedBooking.e_ticket, "_blank")}
                      style={{ padding: 0 }}
                    >
                      View Ticket
                    </Button>
                  ) : "N/A"}
                </Descriptions.Item>
                {selectedBooking.boarding_passes && selectedBooking.boarding_passes.length > 0 && (
                  <Descriptions.Item label="Boarding Passes" span={screens.xs ? 1 : 2}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {selectedBooking.boarding_passes.map((pass, index) => (
                        <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", borderBottom: index < selectedBooking.boarding_passes.length - 1 ? "1px solid #f0f0f0" : "none", paddingBottom: "8px", paddingTop: index > 0 ? "8px" : "0" }}>
                          <Text>PNR: <Text strong>{pass.pnr || "N/A"}</Text></Text>
                          {pass.boarding_pass_image ? (
                            <Button
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={() => window.open(pass.boarding_pass_image, "_blank")}
                              style={{ padding: 0, height: "auto" }}
                            >
                              View Boarding Pass
                            </Button>
                          ) : (
                            <Text type="secondary">No Image</Text>
                          )}
                        </div>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Title level={5} style={{ marginTop: 24 }}>
                Booking Details
              </Title>
              <Descriptions
                bordered
                column={screens.xs ? 1 : 2}
                size={screens.xs ? "small" : "default"}
                labelStyle={{ width: screens.xs ? 110 : "auto" }}
              >
                <Descriptions.Item label="Room Type">
                  {selectedBooking.roomType}
                </Descriptions.Item>
                <Descriptions.Item label="Number of Rooms">
                  {selectedBooking.number_of_Rooms}
                </Descriptions.Item>
                {/* <Descriptions.Item label="Room Fare">
                  ${selectedBooking.room_fare}
                </Descriptions.Item> */}
                <Descriptions.Item label="Check-in">
                  {moment(selectedBooking.checkIn).format("DD MMM YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Check-out">
                  {moment(selectedBooking.checkOut).format("DD MMM YYYY")}
                </Descriptions.Item>
                {/* <Descriptions.Item label="Total Amount">
                  ${selectedBooking.room_fare * selectedBooking.number_of_Rooms}
                </Descriptions.Item> */}
              </Descriptions>

              {/* <Title level={5} style={{ marginTop: 24 }}>
                Invoice Services
              </Title> */}
              {/* {(selectedBooking.selected_services || []).length ? (
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(row, idx) => row.service_key || `${row.label}-${idx}`}
                  columns={[
                    { title: "Service", dataIndex: "label", key: "label" },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      key: "amount",
                      align: "right",
                      render: (v) => `$${Number(v || 0)}`,
                    },
                  ]}
                  dataSource={selectedBooking.selected_services || []}
                />
              ) : (
                <Text type="secondary">No extra services selected.</Text>
              )} */}

              {/* <Title level={5} style={{ marginTop: 24 }}>
                Invoice Breakdown
              </Title>
              <Descriptions bordered column={screens.xs ? 1 : 2} size={screens.xs ? "small" : "default"}>
                <Descriptions.Item label="Stay Amount">
                  ${Number(selectedBooking.invoice_breakdown?.stay_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Mandatory Extras">
                  ${Number(selectedBooking.invoice_breakdown?.mandatory_extra_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Selected Extras">
                  ${Number(selectedBooking.invoice_breakdown?.selected_extra_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Service Fee">
                  ${Number(selectedBooking.invoice_breakdown?.service_fee || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Tax">
                  ${Number(selectedBooking.invoice_breakdown?.tax_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Final Total">
                  <Text strong>${Number(selectedBooking.invoice_breakdown?.total || selectedBooking.room_fare || 0)}</Text>
                </Descriptions.Item>
              </Descriptions> */}

              {/* <Title level={5} style={{ marginTop: 24 }}>
                Booked Rooms
              </Title>
              {selectedBooking.bookedRoom?.map((floor, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <Text strong>Floor {floor.floorNumber}</Text>
                  {floor.rooms?.map((room, roomIndex) => (
                    <div key={roomIndex} style={{ marginTop: 8 }}>
                      <Space>
                        <Text>Room {room.roomNumber}</Text>
                        <Tag color={room.available ? "green" : "red"}>
                          {room.available ? "Available" : "Occupied"}
                        </Tag>
                        <Text>Price: ${room.price}</Text>
                        <Text>Type: {room.roomType}</Text>
                      </Space>
                    </div>
                  ))}
                </Card>
              ))}

              <Title level={5} style={{ marginTop: 24 }}>
                Guest Details
              </Title>
              {selectedBooking.guests?.map((guest, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <Space>
                    <Text strong>{guest.firstName} {guest.lastName}</Text>
                    <Text type="secondary">Age: {guest.age}</Text>
                  </Space>
                </Card>
              ))} */}

              <Title level={5} style={{ marginTop: 24 }}>
                Timestamps
              </Title>
              <Descriptions
                bordered
                column={screens.xs ? 1 : 2}
                size={screens.xs ? "small" : "default"}
                labelStyle={{ width: screens.xs ? 110 : "auto" }}
              >
                <Descriptions.Item label="Booked On">
                  {moment(selectedBooking.createdAt).format("DD MMM YYYY, HH:mm")}
                </Descriptions.Item>
                <Descriptions.Item label="Last Updated">
                  {moment(selectedBooking.updatedAt).format("DD MMM YYYY, HH:mm")}
                </Descriptions.Item>
              </Descriptions>

              <Title level={5} style={{ marginTop: 24 }}>
                SMS Activity Logs
              </Title>
              {selectedBooking.sms_logs && selectedBooking.sms_logs.length > 0 ? (
                <Table
                  className="table-responsive"
                  size="small"
                  pagination={false}
                  rowKey={(row) => row._id || row.message_sid}
                  columns={[
                    {
                      title: "Sent At",
                      dataIndex: "sent_at",
                      render: (v) => moment(v).format("DD MMM YYYY, HH:mm")
                    },
                    {
                      title: "Recipient",
                      dataIndex: "phone_number"
                    },
                    {
                      title: "Type",
                      dataIndex: "message_type",
                      render: (t) => t?.replace('_', ' ')?.toUpperCase()
                    },
                    {
                      title: "Message",
                      dataIndex: "body"
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      render: (s) => {
                        let color = "blue";
                        if (s === "delivered" || s === "simulated") color = "green";
                        if (s === "failed" || s === "undelivered") color = "red";
                        return <Tag color={color}>{s?.toUpperCase()}</Tag>;
                      }
                    }
                  ]}
                  dataSource={selectedBooking.sms_logs}
                />
              ) : (
                <Text type="secondary">No SMS activity logged for this booking.</Text>
              )}

              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={handleResendSMS}
                  loading={resendingSMS}
                  style={{
                    borderRadius: 10,
                    background: theme.primaryDark,
                    borderColor: theme.primaryDark,
                    fontWeight: 600,
                  }}
                >
                  Resend SMS Confirmations
                </Button>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title="Create New Booking"
          open={createModalVisible}
          onCancel={() => setCreateModalVisible(false)}
          footer={null}
          width={800}
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateSubmit}
          >
            <Title level={5}>Guest Details</Title>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                  <Input placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                  <Input placeholder="Enter last name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="dob" label="Date of Birth">
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
                  <Input placeholder="Enter email" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="phoneNumber" label="Phone Number" rules={[{ required: true }]}>
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={5}>Travel Details</Title>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="airline_id" label="Airline" rules={[{ required: true }]}>
                  <Select
                    placeholder="Select Airline"
                    onChange={handleAirlineChange}
                    disabled={authRole === "airline"}
                    options={airlineOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="adults" label="Adults" initialValue={1}>
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="children" label="Children" initialValue={0}>
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="Hotel_Id" label="Hotel" rules={[{ required: true }]}>
                  <Select
                    placeholder="Select Hotel"
                    onChange={handleHotelChange}
                    options={hotelOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="dates" label="Check-in & Check-out Dates" rules={[{ required: true }]}>
                  <RangePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="bookedRoomsData" label="Room Selection" rules={[{ required: true, message: "Please select at least one room" }]}>
                  <Select
                    mode="multiple"
                    placeholder="Select Rooms"
                    options={roomOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="meals" label="Meal Selection">
                  <Select
                    mode="multiple"
                    placeholder="Select Meals"
                    options={mealOptions}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end">
              <Space>
                <Button onClick={() => setCreateModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Create Booking
                </Button>
              </Space>
            </Row>
          </Form>
        </Modal>


        {/* Accept Booking Modal */}
        <Modal
          title="Accept Booking & Assign Room"
          open={acceptModalVisible}
          onCancel={() => setAcceptModalVisible(false)}
          onOk={() => roomAssignForm.submit()}
          confirmLoading={acceptLoading}
          okText="Accept & Confirm"
          okButtonProps={{ style: { backgroundColor: "#52c41a", borderColor: "#52c41a" } }}
        >
          <Alert
            message="Assign a room to confirm this booking request."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form form={roomAssignForm} layout="vertical" onFinish={handleAcceptConfirm}>
            <Form.Item
              name="roomType"
              label="Select Room Type"
              rules={[{ required: true, message: "Please select a room type" }]}
            >
              <Select placeholder="Choose Type">
                {availableRoomTypes.map((type) => (
                  <Option key={type.type} value={type.type}>
                    {type.type} ({type.Available_rooms} available)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* Reject Booking Modal */}
        <Modal
          title="Reject Booking Request"
          open={rejectModalVisible}
          onCancel={() => setRejectModalVisible(false)}
          onOk={handleRejectConfirm}
          confirmLoading={rejectLoading}
          okText="Reject Booking"
          okButtonProps={{ danger: true }}
        >
          <Text>
            Are you sure you want to reject the booking request <strong>{selectedBooking?.Booking_Id}</strong>?
            This action will notify the customer and cancel the request.
          </Text>
        </Modal>


        <style>{`
          .bookings-page .ant-input,
          .bookings-page .ant-input-affix-wrapper,
          .bookings-page .ant-select-selector,
          .bookings-page .ant-picker {
            border-radius: 10px !important;
            border-color: ${theme.border} !important;
          }
          .bookings-page .ant-input-affix-wrapper:hover,
          .bookings-page .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
          .bookings-page .ant-picker:hover {
            border-color: rgba(53, 80, 220, 0.45) !important;
          }
          .bookings-page .ant-input-affix-wrapper-focused,
          .bookings-page .ant-select-focused .ant-select-selector,
          .bookings-page .ant-picker-focused {
            border-color: var(--theme1, #eca413) !important;
            box-shadow: 0 0 0 3px rgba(53, 80, 220, 0.15) !important;
          }
          .bookings-page .bookings-table .ant-table-thead > tr > th {
            background: rgba(53, 80, 220, 0.04);
            font-weight: 700;
            color: rgba(0, 0, 0, 0.75);
          }
          .bookings-page .bookings-table .ant-table-tbody > tr:hover > td {
            background: rgba(53, 80, 220, 0.03);
          }
          .bookings-page .ant-btn-primary:hover, .bookings-page .ant-btn-primary:focus {
    background: #eca413bd !important;
    border-color: #eca413de !important;
}
        `}</style>
      </Card>
    </div>
  );
};

export default Bookings;
